/* eslint-disable import/prefer-default-export */
import { ReactNode } from "react";
import { Box, Flex, FlexProps } from "@chakra-ui/react";
import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import type { CellContext, HeaderContext } from "@tanstack/react-table";
import {
  FormatCurrencyOptions,
  makeFormatCurrency,
  makeFormatDate,
  makeFormatDecimal,
  makeFormatInteger,
  makeFormatPercent,
  makeFormatZero,
} from "utils/formatting";
import ExternalDomainLink from "components/Shared/ExternalDomainLink";
import Address, { AddressProps } from "components/Shared/Address";
import DateTime from "components/Shared/DateTime";
import { ZeroDash } from "components/Shared/EmptyPlaceholder";
import SkeletonSingleLineText from "components/Shared/SkeletonSingleLineText";

export const zeroDashFormatter = makeFormatZero(() => <ZeroDash />);

export function makeFooterLoadingCell<TData = unknown, V = unknown>(
  cellFormatter: (cell: HeaderContext<TData, V>) => ReactNode,
  getIsLoading: (cell: HeaderContext<TData, V>) => boolean,
) {
  return function formatLoadingCell(cell: HeaderContext<TData, V>) {
    const isLoading = getIsLoading(cell);
    const hasRows = cell.table.getRowModel().rows.length > 0;
    return (
      <SkeletonSingleLineText
        isLoaded={hasRows || !isLoading}
        text={cellFormatter(cell)}
        isTextPresenceIgnored
        display="inline-block"
        minSkeletonWidth="3em"
      />
    );
  };
}

export function makeCellFormatter<V = unknown>(
  formatter: (value: V) => ReactNode,
  fallback: ReactNode = null,
) {
  return function formatCell<TData = unknown>(cell: CellContext<TData, V>) {
    return formatter(cell.getValue()) ?? fallback;
  };
}

export function makeFormatDecimalCell(
  options?: Intl.NumberFormatOptions,
  fallback: ReactNode = null,
) {
  return makeCellFormatter(makeFormatDecimal(options), fallback);
}

export function makeFormatCurrencyCell(
  options?: FormatCurrencyOptions,
  fallback: ReactNode = null,
) {
  return makeCellFormatter(makeFormatCurrency(options), fallback);
}

export const formatCurrencyCell = makeFormatCurrencyCell();

export function makeFormatPercentCell(
  options?: Intl.NumberFormatOptions,
  fallback: ReactNode = null,
) {
  return makeCellFormatter(makeFormatPercent(options), fallback);
}

export const formatPercentCell = makeFormatPercentCell();

export function makeFormatIntegerCell(fallback: ReactNode = null) {
  return makeCellFormatter(makeFormatInteger(), fallback);
}

export const formatIntegerCell = makeFormatIntegerCell();

export function makeFormatDateCell(format: string, fallback: ReactNode = null) {
  return makeCellFormatter(makeFormatDate(format), fallback);
}

export const formatDateCell = makeFormatDateCell("PPP");

export function makeFormatDateTimeCell(
  format: string,
  fallback: ReactNode = null,
) {
  return makeCellFormatter((value: ISODateTime | null | undefined) => {
    if (!value) return fallback;
    return <DateTime datetime={value} format={format} />;
  });
}

export const formatDateTimeCell = makeFormatDateTimeCell("PPp");

type JustifiedDateTimeProps = Omit<FlexProps, "dateTime"> & {
  dateFormat?: string;
  datetime: ISODateTime;
  timeFormat?: string;
};

function JustifiedDateTime({
  dateFormat = "PP",
  datetime,
  timeFormat = "p",
  ...rest
}: JustifiedDateTimeProps) {
  return (
    <Flex
      as="time"
      display="inline-flex"
      whiteSpace="nowrap"
      justifyContent="space-between"
      flexWrap="wrap"
      width="100%"
      maxW="15em"
      dateTime={datetime}
      {...rest}
    >
      <Box as="span" isTruncated mr=".5em">
        {makeFormatDate(dateFormat)(datetime)}
      </Box>
      <Box as="span" ml="auto">
        {makeFormatDate(timeFormat)(datetime)}
      </Box>
    </Flex>
  );
}

export function makeFormatJustifiedDateTimeCell({
  fallback = null,
  ...options
}: Omit<JustifiedDateTimeProps, "datetime"> & { fallback?: ReactNode } = {}) {
  return makeCellFormatter((value: ISODateTime | null | undefined) => {
    if (!value) return fallback;
    return <JustifiedDateTime datetime={value} {...options} />;
  });
}

export const formatJustifiedDateTimeCell = makeFormatJustifiedDateTimeCell();

type BooleanCellProps = {
  value: boolean | null | undefined;
};

export function BooleanCell({ value }: BooleanCellProps) {
  if (value) return <CheckIcon />;
  if (value === false) return <CloseIcon opacity={0.3} />;
  return null;
}

export function makeFormatBooleanCell() {
  return makeCellFormatter((value: boolean | null | undefined) => (
    <BooleanCell value={value} />
  ));
}

export const formatBooleanCell = makeFormatBooleanCell();

function makeFormatExternalDomainCell() {
  return makeCellFormatter((value: string | null | undefined) =>
    value ? <ExternalDomainLink href={value} /> : null,
  );
}

export const formatExternalDomainCell = makeFormatExternalDomainCell();

export function makeFormatEnumCell<E extends string | number | symbol>(
  labelsByEnumValue: Record<E, string>,
) {
  return makeCellFormatter((value: E | null | undefined) =>
    value ? labelsByEnumValue[value] : null,
  );
}

export function makeFormatAddressCell(
  props?: Pick<AddressProps, "isMultiLine">,
) {
  return makeCellFormatter((value: AddressProps["address"]) => (
    <Address address={value} isTruncated {...props} />
  ));
}

export const formatAddressCell = makeFormatAddressCell();
